
import React, { useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Text, Edges, RoundedBox } from "@react-three/drei";

/**
 * Calcule position + rotation du texte selon la face du cube.
 * Faces possibles :
 *  - "front"  (Z+)
 *  - "back"   (Z-)
 *  - "right"  (X+)
 *  - "left"   (X-)
 *  - "top"    (Y+)
 *  - "bottom" (Y-)
 */
function getFaceTransform(face = "front") {
  switch (face) {
    case "back":
      return {
        position: [0, 0, -0.51],
        rotation: [0, Math.PI, 0],
      };
    case "right":
      return {
        position: [0.51, 0, 0],
        rotation: [0, -Math.PI / 2, 0],
      };
    case "left":
      return {
        position: [-0.51, 0, 0],
        rotation: [0, Math.PI / 2, 0],
      };
    case "top":
      return {
        position: [0, 0.51, 0],
        rotation: [-Math.PI / 2, 0, 0],
      };
    case "bottom":
      return {
        position: [0, -0.51, 0],
        rotation: [Math.PI / 2, 0, 0],
      };
    case "front":
    default:
      return {
        position: [0, 0, 0.51],
        rotation: [0, 0, 0],
      };
  }
}

function Cube({
  index,
  position,
  color = "white",
  hoverColor = "#fb923c",
  faces = [],
  onCubeClick,
  token,
  showTokens = false,
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <group
      position={position}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        setHovered(false);
      }}
      onClick={(e) => {
        e.stopPropagation();
        onCubeClick?.(index);
      }}
    >
      <RoundedBox args={[1, 1, 1]} radius={0.18} smoothness={4}>
        <meshStandardMaterial attach="material" color={hovered ? hoverColor : color} />
        {hovered && <Edges scale={1.03} color="black" />}
      </RoundedBox>

      {faces.map(({ face, text }, i) => {
        const { position: textPos, rotation: textRot } = getFaceTransform(face);
        return (
          <Text
            key={i}
            position={textPos}
            rotation={textRot}
            fontSize={1.1}
            color="black"
            anchorX="center"
            anchorY={-0.8}
            depthTest={false}
            renderOrder={10}
            frustumCulled={false}
          >
            {text}
          </Text>
        );
      })}

      {/* ✅ Token daltonien-friendly */}
      {showTokens && token && (
        <Text
          position={[0, 0.92, 0]}
          fontSize={0.28}
          color="white"
          anchorX="center"
          anchorY="middle"
          depthTest={false}
          renderOrder={20}
          frustumCulled={false}
        >
          {token}
        </Text>
      )}
    </group>
  );
}

/**
 * Trois dés alignés
 * (ici j'utilise les glyphes ⚀⚁⚂⚃⚄⚅ comme motifs)
 */
function CubeRow({ onCubeClick, showTokens }) {
  return (
    <>
      <Cube
        index={0}
        position={[-1.6, 0, 0]}
        color="white"
        hoverColor="#f1a56e" // orange
        token="O"
        showTokens={showTokens}
        faces={[
          { face: "front", text: "⚂" },
          { face: "back", text: "⚁" },
          { face: "left", text: "" },
          { face: "right", text: "⚅" },
          { face: "top", text: "⚀" },
          { face: "bottom", text: "⚃" },
        ]}
        onCubeClick={onCubeClick}
      />

      <Cube
        index={1}
        position={[0, 0, 0]}
        color="white"
        hoverColor="#f0a5e9" // rose
        token="R"
        showTokens={showTokens}
        faces={[
          { face: "front", text: "⚃" },
          { face: "back", text: "" },
          { face: "left", text: "⚁" },
          { face: "right", text: "⚄" },
          { face: "top", text: "⚅" },
          { face: "bottom", text: "⚂" },
        ]}
        onCubeClick={onCubeClick}
      />

      <Cube
        index={2}
        position={[1.6, 0, 0]}
        color="white"
        hoverColor="#9abbf1" // bleu
        token="B"
        showTokens={showTokens}
        faces={[
          { face: "front", text: "⚄" },
          { face: "back", text: "⚅" },
          { face: "left", text: "⚁" },
          { face: "right", text: "⚃" },
          { face: "top", text: "" },
          { face: "bottom", text: "⚀" },
        ]}
        onCubeClick={onCubeClick}
      />
    </>
  );
}

export default function CubeLock({ onCubeClick, showTokens = false }) {
  return (
    <div style={{ width: "100%", height: 320, borderRadius: "12px", overflow: "hidden", background: "#020617" }}>
      <Canvas camera={{ position: [4, 2.5, 6], fov: 40 }}>
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 5, 5]} intensity={1.2} />
        <pointLight position={[-4, -3, -4]} intensity={0.8} />

        <CubeRow onCubeClick={onCubeClick} showTokens={showTokens} />

        <OrbitControls makeDefault enablePan={false} minDistance={3} maxDistance={8} />
      </Canvas>
    </div>
  );
}

